import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getAllProductLossesLCA,
  addProductLossesLCA,
  updateProductLossesLCA,
  deleteProductLossesLCA,
} from "../../../../../../apis/Product/LossesApi";
import { getProductLossesMaterialDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import EditableResultDropdownAutocomplete from "../../../../../../components/EditableResultDropdownAutocomplete";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration - All 16 categories
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  {
    name: "Photochemical ozone formation",
    unit: "kg NMVOC-eq",
    key: "smog",
  },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  {
    name: "Ecotoxicity, freshwater",
    unit: "CTUe",
    key: "ecotoxicity",
  },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  {
    name: "Water use",
    unit: "m³ world-eq deprived",
    key: "water_use",
  },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const ProdLossesTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);

  // Fetch material dropdown data
  useEffect(() => {
    getProductLossesMaterialDropdown({
      setDropdown: setMaterialDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllProductLossesLCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialCode =
          typeof row.material === "string"
            ? row.material
            : row.material?.value || row.material;
        if (materialCode) {
          const selectedMaterial = materialDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === materialCode ||
                option.value === materialCode ||
                option.name === materialCode)
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit || "unit";
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, materialDropdown]);

  // Navigation handlers for impact categories
  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev > 0 ? prev - 1 : IMPACT_CATEGORIES.length - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev < IMPACT_CATEGORIES.length - 1 ? prev + 1 : 0
    );
  }, []);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const currentImpactKey = currentImpactCategory.key;

  // Helper function to get matching option from dropdown
  const getMatchingOption = useCallback(
    (value) => {
      if (!value) return null;
      if (typeof value === "object") return value;
      return (
        materialDropdown.find(
          (option) =>
            !option.isCategory &&
            (option.code === value || option.value === value || option.name === value)
        ) || null
      );
    },
    [materialDropdown]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "own_name",
        header: t("OWN_NAME") || "Own name",
        type: "text",
        disableSortBy: true,
        width: "200px",
        placeholder: t("USER_TYPES") || "user types...",
      },
      {
        accessor: "material",
        header: `${t("MATERIAL") || "Material"}*`,
        type: "dropdown",
        isCategory: true,
        dropdownData: materialDropdown,
        disableSortBy: true,
        width: "250px",
        unit: (rowId, rowData) => {
          const materialCode =
            typeof rowData?.material === "string"
              ? rowData.material
              : rowData?.material?.value || rowData?.material;
          if (materialCode) {
            const selectedMaterial = materialDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === materialCode ||
                  option.value === materialCode ||
                  option.name === materialCode)
            );
            return selectedMaterial?.unit || unitMaterials[rowId] || "unit";
          }
          return unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: "amount",
        header: `${t("AMOUNT") || "Amount"}*`,
        type: "number",
        disableSortBy: true,
        width: "250px",
        placeholder: t("USER_TYPES") || "user types...",
        unit: (rowId, rowData) => {
          const materialCode =
            typeof rowData?.material === "string"
              ? rowData.material
              : rowData?.material?.value || rowData?.material;
          if (materialCode) {
            const selectedMaterial = materialDropdown.find(
              (option) =>
                !option.isCategory &&
                (option.code === materialCode ||
                  option.value === materialCode ||
                  option.name === materialCode)
            );
            return selectedMaterial?.unit || unitMaterials[rowId] || "unit";
          }
          return unitMaterials[rowId] || "unit";
        },
      },
      {
        accessor: currentImpactKey,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span>{currentImpactCategory.name}</span>
              <span style={{ fontSize: "12px", color: "#666" }}>
                {currentImpactCategory.unit}
              </span>
            </div>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        type: "custom",
        disableSortBy: true,
        width: "200px",
        optional: true, // Not required - calculated output only
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] || // Direct field from API (e.g., global_warming)
            row.original[`impact_${currentCategory.key}`] || // Fallback for nested structure
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
      },
    ],
    [
      t,
      materialDropdown,
      unitMaterials,
      currentImpactCategoryIndex,
      currentImpactCategory,
      currentImpactKey,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const materialValue =
        editedItem.material?.value ||
        editedItem.material ||
        editedItem.input?.value ||
        editedItem.input;

      const foundMaterial = materialDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === materialValue ||
            option.code === materialValue ||
            option.name === materialValue)
      );

      const materialCode = foundMaterial?.code || materialValue;

      updateProductLossesLCA({
        productId,
        lossesId: editedItem.id,
        own_name: editedItem.own_name,
        material: materialCode,
        amount: editedItem.amount,
        objects: data,
        setObjects: setData,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, materialDropdown, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialValue =
        newItem.material?.value ||
        newItem.material ||
        newItem.input?.value ||
        newItem.input;

      const foundMaterial = materialDropdown?.find(
        (option) =>
          !option.isCategory &&
          (option.value === materialValue ||
            option.code === materialValue ||
            option.name === materialValue)
      );

      const materialCode = foundMaterial?.code || materialValue;

      addProductLossesLCA({
        productId,
        own_name: newItem.own_name,
        material: materialCode,
        amount: newItem.amount,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData, materialDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductLossesLCA({
        productId,
        lossesId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const disableRules = useMemo(() => {
    const rules = {};
    IMPACT_CATEGORIES.forEach((category) => {
      rules[category.key] = () => true; // All impact categories are read-only
    });
    return rules;
  }, []);

  // Dynamic placeholders to update unit when material is selected
  const dynamicPlaceholders = useMemo(
    () => ({
      material: {
        targetField: "amount",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) =>
              !option.isCategory &&
              (option.value === selectedValue ||
                option.code === selectedValue ||
                option.name === selectedValue)
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit || "xx",
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit || "xx",
              }));
            }
          }
        },
        dropdownDataKey: "material",
      },
    }),
    []
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={{
        material: materialDropdown,
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
    />
  );
};

export default ProdLossesTable;

